import React from 'react';
import { EventDto } from 'stores/events/events-store';
import { RequestDto } from 'stores/requests/requests-store';

interface FullMonthViewProps {
  events: EventDto[];
  daysInMonth: number;
  currentDate: Date;
  selectedDate: Date | null;
  isBusy: boolean;
  requests: RequestDto[];
  entityType: 'events' | 'requests';
  onSelectDate: (date: Date) => void;
}

const FullMonthView: React.FC<FullMonthViewProps> = ({
  events,
  daysInMonth,
  currentDate,
  selectedDate,
  isBusy,
  requests,
  entityType,
  onSelectDate
}) => {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const getEventsForDay = (day: Date) => {
    const dayEvents = events.filter(event => {
      const eventStart = new Date(event.startDate);
      const eventEnd = new Date(event.endDate);
      eventStart.setHours(0, 0, 0, 0);
      eventEnd.setHours(0, 0, 0, 0);
      day.setHours(0, 0, 0, 0);
      return day >= eventStart && day <= eventEnd;
    });

    return (
      <div className="flex justify-center gap-1 pt-1">
        {dayEvents.length > 0 && (
          <div className="flex gap-1">
            {dayEvents.slice(0, 3).map((event, index) => (
              <span
                key={index}
                className="w-1 h-1 rounded-full"
                style={{ backgroundColor: event.eventType?.eventsColor || '#3B82F6' }}
                title={event.title}
              />
            ))}
            {dayEvents.length > 3 && <span className="w-1 h-1 bg-gray-400 rounded-full" title={`+${dayEvents.length - 3} more`} />}
          </div>
        )}
      </div>
    );
  };

  const getRequestsForDay = (day: Date) => {
    const dayRequests = requests.filter(request => {
      const requestStart = new Date(request.startDate);
      const requestEnd = new Date(request.endDate);
      requestStart.setHours(0, 0, 0, 0);
      requestEnd.setHours(0, 0, 0, 0);
      day.setHours(0, 0, 0, 0);
      return day >= requestStart && day <= requestEnd;
    });

    return (
      <div className={`flex justify-center gap-1 pt-1`}>
        {dayRequests.length > 0 && (
          <div className="flex gap-1">
            {dayRequests.slice(0, 3).map((request, index) => (
              <span
                key={index}
                className="w-1 h-1 rounded-full"
                style={{ backgroundColor: request.eventType?.eventsColor || '#3B82F6' }}
                title={request.title}
              />
            ))}
            {dayRequests.length > 3 && <span className="w-1 h-1 bg-gray-400 rounded-full" title={`+${dayRequests.length - 3} more`} />}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`grid grid-cols-7 gap-1 p-2 ${isBusy ? 'opacity-50 pointer-events-none' : ''}`}>
      {Array.from({ length: daysInMonth }, (_, i) => {
        const day = new Date(year, month, i + 1);
        const isSelected = selectedDate?.toDateString() === day.toDateString();
        return (
          <div
            key={i}
            onClick={() => !isBusy && onSelectDate(day)}
            className={`p-2 border border-gray-300 rounded shadow-md text-sm 
              ${isSelected ? 'bg-gray-300' : ''}
              ${isBusy ? 'cursor-not-allowed bg-gray-50' : 'cursor-pointer hover:bg-gray-100'}`}
          >
            <div className={`${isBusy ? 'text-gray-400' : ''}`}>{i + 1}</div>
            {entityType === 'events' ? getEventsForDay(day) : getRequestsForDay(day)}
          </div>
        );
      })}
    </div>
  );
};

export default FullMonthView;
